import {Button, Form, Input, Select, Spin} from 'antd'
import {docs_addCodiDocumentRow_Api, docs_editCodiDocumentRow_Api, docs_supplyCodiDocumentRow_Api,} from 'apis'
import classNames from 'classnames'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useMemo, useState} from 'react'
import {HiCode} from 'react-icons/hi'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault} from 'views/Shared'
import {CodiDocumentContentTypes, useRowDetails} from '../CodiDocumentContext'
import AdjPicker from '../components/Codi/AdjPicker'
import CodiDocumentMeta from '../components/Codi/CodiDocumentMeta'
import {LoadingPlaceholder} from '../functions/renderLoading'
import {CodiDocumentRowSelector, CodiDocumentSelector,} from './CodiDocumentSelector'

const FormItem = ({ label, ...props }) => (
  <Form.Item
    label={
      label ? (
        <Translate>
          {(t) => (
            <span className="capitalize text-sm font-semibold">{t(label)}</span>
          )}
        </Translate>
      ) : undefined
    }
    style={{
      marginBottom: 10,
    }}
    {...props}
  />
)

export const InsertCodiContent = ({
  id,
  params,
  Wrapper = 'div',
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
  renderActions = NullAsDefault,
}) => {
  const t = useTranslate()

  const { isSm } = useContext(LayoutContext)

  const [isSubmitting, setSubmitting] = useState(false)

  const { index_typecd } = params || {}

  const row_id = params?.id

  const [form_values, set_form_values] = useState({})

  const [content_type, set_content_type] = useState()

  const { item, isLoading = false } = useRowDetails(row_id)

  const addingRow = useAsyncAction({
    apiInfo: docs_addCodiDocumentRow_Api,
    query: bindQueryParams([{ id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.CREATE, t)
      setSubmitting(false)
      onSuccess()
    },
    onError: (e, o) => {
      setSubmitting(false)
      notifyOnError(t)(e, o)
    },
  })

  const addingByRefRow = useAsyncAction({
    apiInfo: docs_supplyCodiDocumentRow_Api,
    query: bindQueryParams([{ id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.CREATE, t)
      setSubmitting(false)
      onSuccess()
    },
    onError: (e, o) => {
      setSubmitting(false)
      notifyOnError(t)(e, o)
    },
  })

  const transform = (name) => ({ label: t(name), value: name })

  const content_types = useMemo(() => {
    let options = []
    switch (index_typecd) {
      case CodiDocumentContentTypes.ROOT:
        options = [
          CodiDocumentContentTypes.PART,
          CodiDocumentContentTypes.APPENDIX,
          CodiDocumentContentTypes.CHAPTER,
          CodiDocumentContentTypes.ARTICLE,
          CodiDocumentContentTypes.CONTENT,
        ]
        break
      case CodiDocumentContentTypes.PART:
      case CodiDocumentContentTypes.APPENDIX:
        options = [
          CodiDocumentContentTypes.CHAPTER,
          CodiDocumentContentTypes.ARTICLE,
          CodiDocumentContentTypes.CONTENT,
        ]
        break
      case CodiDocumentContentTypes.CHAPTER:
        options = [
          CodiDocumentContentTypes.SECTION,
          CodiDocumentContentTypes.ARTICLE,
          CodiDocumentContentTypes.CONTENT,
        ]
        break
      case CodiDocumentContentTypes.SECTION:
        options = [
          CodiDocumentContentTypes.ARTICLE,
          CodiDocumentContentTypes.CONTENT,
        ]
        break
      case CodiDocumentContentTypes.ARTICLE:
        options = [
          CodiDocumentContentTypes.CLAUSE,
          CodiDocumentContentTypes.CONTENT,
        ]
        break
      case CodiDocumentContentTypes.CLAUSE:
        options = [
          CodiDocumentContentTypes.POINT,
          CodiDocumentContentTypes.CONTENT,
        ]
        break
      case CodiDocumentContentTypes.POINT:
        options = [CodiDocumentContentTypes.CONTENT]
        break
      default:
        break
    }
    return options
  }, [index_typecd])

  const [form] = Form.useForm()

  const handleSubmit = async (values) => {
    setSubmitting(true)

    const { content, index_cd, is_html, adj_document_row_id } = values || {}

    const { handleAsyncAction = NullAsDefault } = addingRow || {}

    const { codi_document, ...rest } = form_values || {}

    handleAsyncAction(
      _.omitBy(
        {
          is_html: _.defaultTo(Number(is_html), 0),
          content,
          index_cd,
          adj_document_row_id,
          index_typecd: content_type,
          ...rest,
        },
        _.isUndefined
      )
    )
  }

  const initialValues = {
    is_html: false,
    ..._.omit(item, ['content']),
  }

  const options = useMemo(() => content_types.map(transform), [content_types])

  return (
    <Form
      form={form}
      key={item}
      layout="vertical"
      className="flex flex-col flex-1"
      onFinish={handleSubmit}
      initialValues={initialValues}>
      <Wrapper className="flex flex-col flex-1 mx-auto max-w-2xl w-full gap-2 py-2 px-8 custom-form-codi">
        <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
          {t('Embed a section of a document')}
        </div>
        <LoadingPlaceholder isLoading={isLoading}>
          <div className="flex items-baseline gap-2 border-b">
            <span className="capitalize italic text-sm text-color-400">
              {t('document')}
              {' : '}
            </span>
            <span className="flex-1 font-bold text-doc-lg text-primary">
              {[_.get(item, 'root_cd'), _.get(item, 'root_content')].join(' ')}
            </span>
          </div>
          <div className="text-sm space-x-2">
            <span className="text-color-000 font-bold">
              {_.get(item, 'short_index_name')}
              {':'}
            </span>
            {Boolean(_.get(item, 'is_html')) ? (
              <div className="flex flex-col flex-center gap-1 py-3 background-100 rounded-lg mt-2">
                <HiCode
                  size={30}
                  className="text-color-400"
                />
                <span className="text-xs text-color-500 italic">
                  {t('here is the HTML code')}
                </span>
              </div>
            ) : (
              <span className="text-color-100 font-medium">
                {_.get(item, 'content')}
              </span>
            )}
          </div>
        </LoadingPlaceholder>
        <Spin spinning={!!isLoading}>
          <div className="flex flex-col mb-3">
            <FormItem label="type-cd">
              <SelectWidget
                className="w-full"
                allowClear={true}
                options={options}
                placeholder={
                  <span className="text-xs italic text-color-400">
                    {t('ex:type-cd')}
                  </span>
                }
                onChange={(value) => {
                  set_content_type(value ?? null)
                  form.setFieldsValue(initialValues)
                }}
              />
            </FormItem>
            <FormItem
              name="adj_document_row_id"
              label="reference document">
              <div className="flex flex-col gap-2 ">
                <CodiDocumentSelector
                  current_row_id={row_id}
                  placeholder={
                    <span className="text-xs italic text-color-400">
                      {t('codi document')}
                    </span>
                  }
                  onChange={(item, option) => {
                    set_form_values({
                      ...form_values,
                      codi_document: option,
                    })
                    if (!item) {
                      form.setFieldsValue({
                        adj_document_row_id: undefined,
                      })
                    }
                  }}
                />
                {form_values.codi_document && (
                  <CodiDocumentRowSelector
                    placeholder={
                      <span className="text-xs italic text-color-400">
                        {t('ex:type-cd')}
                      </span>
                    }
                    onChange={(item) => {
                      form.setFieldsValue({
                        adj_document_row_id: _.get(item, 'value'),
                      })
                    }}
                    codi_document={form_values.codi_document}
                  />
                )}
              </div>
            </FormItem>
          </div>

          <div className="border-b-2 border-color-300 font-semibold uppercase text-doc-lg text-color-200 mb-3">
            {t('content')}
          </div>
          {[
            {
              name: CodiDocumentContentTypes.PART,
              label: 'part-cd',
              fieldNames: ['part_cd', 'part_content'],
            },
            {
              name: CodiDocumentContentTypes.APPENDIX,
              label: 'appendix-cd',
              fieldNames: ['appendix_cd', 'appendix_content'],
            },
            {
              name: CodiDocumentContentTypes.CHAPTER,
              label: 'chapter-cd',
              fieldNames: ['chapter_cd', 'chapter_content'],
            },
            {
              name: CodiDocumentContentTypes.SECTION,
              label: 'section-cd',
              fieldNames: ['section_cd', 'section_content'],
            },
            {
              name: CodiDocumentContentTypes.ARTICLE,
              label: 'article-cd',
              fieldNames: ['article_cd', 'article_content'],
            },
            {
              name: CodiDocumentContentTypes.CLAUSE,
              fieldNames: ['clause_cd', 'clause_content'],
              label: 'clause-cd',
              renderAddonBefore: () => (
                <span className="capitalize text-xs font-medium">
                  {t('clause')}
                </span>
              ),
            },
            {
              name: CodiDocumentContentTypes.POINT,
              fieldNames: ['point_cd', 'point_content'],
              label: 'point-cd',
              renderAddonBefore: () => (
                <span className="capitalize text-xs font-medium">
                  {t('point')}
                </span>
              ),
            },
          ].map(
            ({
              name,
              label,
              fieldNames: [cd, content] = [,],
              renderAddonBefore = () => undefined,
            }) => {
              const disabled = !content_type || content_type !== name
              return (
                <FormItem
                  key={name}
                  label={label}>
                  <div className="grid grid-cols-12 gap-2 custom-input-codi">
                    <div className="col-span-12 md:col-span-3">
                      <FormItem
                        name={cd}
                        noStyle={true}>
                        <Input
                          placeholder={t(cd)}
                          disabled={disabled}
                          onChange={(event) => {
                            const { value } = event.target
                            set_form_values({
                              ...form_values,
                              index_cd: value,
                            })
                          }}
                          addonBefore={renderAddonBefore()}
                        />
                      </FormItem>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                      <FormItem
                        name={content}
                        noStyle={true}>
                        <Input
                          disabled={disabled}
                          placeholder={t(content)}
                          onChange={(event) => {
                            const { value } = event.target
                            set_form_values({
                              ...form_values,
                              content: value,
                            })
                          }}
                        />
                      </FormItem>
                    </div>
                  </div>
                </FormItem>
              )
            }
          )}

          <div className="grid grid-cols-12 md:gap-2 custom-input-codi">
            <FormItem
              name="is_html"
              initialValue={0}
              label="content"
              className="col-span-12 md:col-span-3">
              <SelectWidget
                className="mb-2"
                key={initialValues?.is_html}
                placeholder={t('content type')}
                defaultValue={!!initialValues?.is_html}
                disabled={content_type !== CodiDocumentContentTypes.CONTENT}>
                <Select.Option value={true}>{t('html')}</Select.Option>
                <Select.Option value={false}>{t('text')}</Select.Option>
              </SelectWidget>
            </FormItem>
            <FormItem
              name="content"
              label={isSm ? undefined : ' '}
              className="col-span-12 md:col-span-9">
              <FormItem
                name="content"
                noStyle={true}>
                <Input.TextArea
                  placeholder={t('content')}
                  onChange={(event) => {
                    const { value } = event.target
                    set_form_values({
                      ...form_values,
                      content: value,
                    })
                  }}
                  disabled={content_type !== CodiDocumentContentTypes.CONTENT}
                />
              </FormItem>
            </FormItem>
          </div>
        </Spin>
      </Wrapper>
      <div className="flex-1" />
      {renderActions(
        <Button
          type="primary"
          htmlType="submit"
          loading={!!isSubmitting}
          disabled={!!isSubmitting}
          className="rounded-lg no-shadow no-text-shadow font-medium">
          {t('insert')}
        </Button>,
        { isSubmitting }
      )}
    </Form>
  )
}

export const EditCodiContent = ({
  id,
  params,
  Wrapper = 'div',
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
  renderActions = NullAsDefault,
}) => {
  const t = useTranslate()

  const { isSm } = useContext(LayoutContext)

  const [isSubmitting, setSubmitting] = useState(false)

  const { index_typecd } = params || {}

  const row_id = params?.id

  const [form_values, set_form_values] = useState({})

  const [content_type, set_content_type] = useState(index_typecd)

  const { item, isLoading = false } = useRowDetails(row_id)

  const edtingRow = useAsyncAction({
    apiInfo: docs_editCodiDocumentRow_Api,
    query: bindQueryParams([{ id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      setSubmitting(false)
      onSuccess()
    },
    onError: (e, o) => {
      setSubmitting(false)
      notifyOnError(t)(e, o)
    },
  })

  const transform = (name) => ({ label: t(name), value: name })

  const content_types = useMemo(() => {
    return [index_typecd]
  }, [index_typecd])

  const [form] = Form.useForm()

  const handleSubmit = async (values) => {
    setSubmitting(true)

    const { is_html, adj_document_row_id } = values || {}

    const { handleAsyncAction = NullAsDefault } = edtingRow || {}

    const { codi_document, codi_document_row, ...rest } = form_values || {}

    handleAsyncAction(
      _.omitBy(
        {
          is_html: _.defaultTo(Number(is_html), 0),
          adj_document_row_id,
          index_typecd: content_type,
          content:
            index_typecd === CodiDocumentContentTypes.CONTENT
              ? item.content
              : undefined,
          ...rest,
        },
        _.isUndefined
      )
    )
  }

  const initialValues = useMemo(
    () => ({
      ..._.omit(item, ['content']),
    }),
    [item]
  )

  const options = useMemo(() => content_types.map(transform), [content_types])

  const is_content = item?.index_typecd === CodiDocumentContentTypes.CONTENT

  useEffect(() => {
    if (is_content) {
      form.setFieldsValue({
        content: item.content,
        is_html: !!item?.is_html,
      })
    } else {
      form.resetFields()
    }
  }, [index_typecd, item.content, item.index_typecd])

  return (
    <Form
      form={form}
      key={item}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
      className="flex flex-col flex-1">
      <Wrapper className="flex flex-col mx-auto max-w-2xl w-full gap-2 py-2 px-8 custom-form-codi">
        <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
          {t('edit content')}
        </div>
        <LoadingPlaceholder isLoading={isLoading}>
          <div className="flex items-baseline gap-2 border-b">
            <span className="capitalize italic text-sm text-color-400">
              {t('document')}
              {' : '}
            </span>
            <span className="flex-1 font-bold text-doc-lg text-primary">
              {[_.get(item, 'root_cd'), _.get(item, 'root_content')].join(' ')}
            </span>
          </div>
          <div
            className={classNames(
              'text-sm',
              is_content ? 'flex flex-col' : 'space-x-1 italic'
            )}>
            <span className="space-x-1 text-color-000 font-bold">
              {`${_.get(item, 'short_index_name')} : `}
              {is_content && (
                <span className="font-medium text-color-100">
                  {_.get(item, 'index_content')}
                </span>
              )}
            </span>
            {Boolean(_.get(item, 'is_html')) ? (
              <div className="flex flex-col flex-center gap-1 py-3 background-100 rounded-lg mt-2">
                <HiCode
                  size={30}
                  className="text-color-400"
                />
                <span className="text-xs text-color-500 italic">
                  {t('here is the HTML code')}
                </span>
              </div>
            ) : (
              <span className="text-color-300 italic">
                {is_content && (
                  <span className="font-medium text-color-100">
                    {t('content')}
                    {' : '}
                  </span>
                )}
                {_.get(item, 'content')}
              </span>
            )}
          </div>
        </LoadingPlaceholder>
        <Spin spinning={!!isLoading}>
          <div className="flex flex-col mb-3">
            <FormItem label="type-cd">
              <SelectWidget
                disabled={true}
                options={options}
                className="w-full"
                defaultValue={index_typecd}
                placeholder={
                  <span className="text-xs italic text-color-400">
                    {t('ex:type-cd')}
                  </span>
                }
                onChange={(value) => {
                  set_content_type(value || null)
                  form.setFieldsValue(initialValues)
                }}
              />
            </FormItem>
            <FormItem
              name="adj_document_row_id"
              label="reference document">
              <AdjPicker
                current_row_id={row_id}
                onDocumentChange={(item, option) => {
                  set_form_values({
                    ...form_values,
                    codi_document: option,
                    codi_document_row: undefined,
                  })
                  if (!item) {
                    form.setFieldsValue({
                      adj_document_row_id: undefined,
                    })
                  }
                }}
                onRowChange={([item, option]) => {
                  set_form_values({
                    ...form_values,
                    codi_document_row: option,
                  })
                  form.setFieldsValue({
                    adj_document_row_id: _.get(item, 'value'),
                  })
                }}
                codi_document={_.get(form_values, 'codi_document')}>
                {([__, toggle]) => {
                  const { codi_document, codi_document_row } = form_values || {}
                  const { adj_document_row } = initialValues || {}

                  const { ref_document, short_index_name } =
                    adj_document_row || {}

                  const [title, description] = codi_document_row
                    ? [getTitle(codi_document), getTitle(codi_document_row)]
                    : [getTitle(ref_document), short_index_name]
                  const description_map = _.get(
                    adj_document_row,
                    'short_index_name'
                  ) && (
                    <span>
                      <span>
                        {_.get(adj_document_row, 'short_index_name')}
                        {' : '}
                      </span>
                      <span>{_.get(adj_document_row, 'content')}</span>
                    </span>
                  )

                  return (
                    <CodiDocumentMeta
                      title={title}
                      description={description_map}
                      document_no={_.get(ref_document, 'document_no')}
                      onClick={() => toggle(true)}
                    />
                  )
                }}
              </AdjPicker>
            </FormItem>
          </div>

          <div className="border-b-2 border-color-300 font-semibold uppercase text-doc-lg text-color-200 mb-3">
            {t('content')}
          </div>
          {[
            {
              name: CodiDocumentContentTypes.PART,
              label: 'part-cd',
              fieldNames: ['part_cd', 'part_content'],
            },
            {
              name: CodiDocumentContentTypes.APPENDIX,
              label: 'appendix-cd',
              fieldNames: ['appendix_cd', 'appendix_content'],
            },
            {
              name: CodiDocumentContentTypes.CHAPTER,
              label: 'chapter-cd',
              fieldNames: ['chapter_cd', 'chapter_content'],
            },
            {
              name: CodiDocumentContentTypes.SECTION,
              label: 'section-cd',
              fieldNames: ['section_cd', 'section_content'],
            },
            {
              name: CodiDocumentContentTypes.ARTICLE,
              label: 'article-cd',
              fieldNames: ['article_cd', 'article_content'],
            },
            {
              name: CodiDocumentContentTypes.CLAUSE,
              label: 'clause-cd',
              fieldNames: ['clause_cd', 'clause_content'],
              renderAddonBefore: () => (
                <span className="capitalize text-xs font-medium">
                  {t('clause')}
                </span>
              ),
            },
            {
              name: CodiDocumentContentTypes.POINT,
              label: 'point-cd',
              fieldNames: ['point_cd', 'point_content'],
              renderAddonBefore: () => (
                <span className="capitalize text-xs font-medium">
                  {t('point')}
                </span>
              ),
            },
          ].map(
            ({
              name,
              label,
              fieldNames: [cd, content] = [,],
              renderAddonBefore = () => undefined,
            }) => {
              const disabled = !content_type || content_type !== name
              return (
                <FormItem
                  key={name}
                  label={label}>
                  <div className="grid grid-cols-12 gap-2 custom-input-codi">
                    <div className="col-span-12 md:col-span-3">
                      <FormItem
                        name={cd}
                        noStyle={true}>
                        <Input
                          disabled={true}
                          placeholder={t(cd)}
                          addonBefore={renderAddonBefore()}
                        />
                      </FormItem>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                      <FormItem
                        name={content}
                        noStyle={true}>
                        <Input
                          disabled={disabled}
                          placeholder={t(content)}
                          onChange={(event) => {
                            const { value } = event.target
                            set_form_values({
                              ...form_values,
                              content: value,
                            })
                          }}
                        />
                      </FormItem>
                    </div>
                  </div>
                </FormItem>
              )
            }
          )}
          <div className="grid grid-cols-12 md:gap-2 custom-input-codi">
            <FormItem
              name="is_html"
              label="content"
              className="col-span-12 md:col-span-3">
              <SelectWidget
                className="mb-2"
                placeholder={t('content type')}
                disabled={content_type !== CodiDocumentContentTypes.CONTENT}>
                <Select.Option value={true}>{t('html')}</Select.Option>
                <Select.Option value={false}>{t('text')}</Select.Option>
              </SelectWidget>
            </FormItem>
            <FormItem
              name="content"
              label={isSm ? undefined : ' '}
              className="col-span-12 md:col-span-9">
              <FormItem
                name="content"
                noStyle={true}>
                <Input.TextArea
                  placeholder={t('content')}
                  onChange={(event) => {
                    const { value } = event.target
                    set_form_values({
                      ...form_values,
                      content: value,
                    })
                  }}
                  disabled={content_type !== CodiDocumentContentTypes.CONTENT}
                />
              </FormItem>
            </FormItem>
          </div>
        </Spin>
      </Wrapper>
      <div className="flex-1" />
      {renderActions(
        <Button
          type="primary"
          htmlType="submit"
          loading={!!isSubmitting}
          disabled={!!isSubmitting}
          className="rounded-lg no-shadow no-text-shadow font-medium">
          {t('save')}
        </Button>,
        { isSubmitting }
      )}
    </Form>
  )
}
